import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  Divider,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
  IconButton,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  ArrowBack,
  ShoppingCart,
  Person,
  Business,
  CalendarToday,
  AttachMoney,
  Assignment,
  Phone,
  Email,
  LocationOn,
  CheckCircle,
  Schedule,
  Cancel,
  Edit,
  Print,
  Share,
} from '@mui/icons-material';
import { apiService, Order } from '../../services/apiService';

const OrderDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      fetchOrderDetails();
    }
  }, [id]);

  const fetchOrderDetails = async () => {
    try {
      setLoading(true);
      const response = await apiService.getOrder(parseInt(id!));
      setOrder(response.data);
    } catch (error: any) {
      console.error('Failed to fetch order details:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return { bg: '#fef3c7', color: '#d97706', icon: <Schedule /> };
      case 'in_progress':
        return { bg: '#dbeafe', color: '#2563eb', icon: <Assignment /> };
      case 'completed':
        return { bg: '#e0f2f1', color: '#0d9488', icon: <CheckCircle /> };
      case 'cancelled':
        return { bg: '#fee2e2', color: '#dc2626', icon: <Cancel /> };
      default:
        return { bg: '#f3f4f6', color: '#6b7280', icon: <Schedule /> };
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED'
    }).format(amount);
  };

  const handlePrint = () => {
    if (!order) {
      console.error('No order data available for printing');
      return;
    }

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Order ${order.teckit_no} - Print</title>
            <style>
              body {
                font-family: 'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif;
                margin: 0;
                padding: 20px;
                background: white;
                color: #1f2937;
              }
              .header {
                text-align: center;
                border-bottom: 2px solid #e5e7eb;
                padding-bottom: 20px;
                margin-bottom: 30px;
              }
              .header h1 {
                color: #1e40af;
                margin: 0;
                font-size: 28px;
                font-weight: 600;
              }
              .header p {
                color: #6b7280;
                margin: 5px 0 0 0;
                font-size: 14px;
              }
              .content {
                display: flex;
                gap: 30px;
              }
              .left-column {
                flex: 2;
              }
              .right-column {
                flex: 1;
              }
              .section {
                margin-bottom: 30px;
                padding: 20px;
                border: 1px solid #e5e7eb;
                border-radius: 8px;
                background: #f9fafb;
              }
              .section h2 {
                color: #1f2937;
                margin: 0 0 15px 0;
                font-size: 18px;
                font-weight: 600;
                border-bottom: 1px solid #e5e7eb;
                padding-bottom: 10px;
              }
              .info-grid {
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 15px;
              }
              .info-item {
                margin-bottom: 10px;
              }
              .info-label {
                font-size: 12px;
                color: #6b7280;
                font-weight: 500;
                margin-bottom: 5px;
                text-transform: uppercase;
                letter-spacing: 0.5px;
              }
              .info-value {
                font-size: 14px;
                color: #1f2937;
                font-weight: 600;
              }
              .status-badge {
                display: inline-block;
                padding: 4px 12px;
                border-radius: 20px;
                font-size: 12px;
                font-weight: 600;
                text-transform: uppercase;
                letter-spacing: 0.5px;
              }
              .status-pending {
                background: #fef3c7;
                color: #d97706;
              }
              .status-in_progress {
                background: #dbeafe;
                color: #2563eb;
              }
              .status-completed {
                background: #e0f2f1;
                color: #0d9488;
              }
              .status-cancelled {
                background: #fee2e2;
                color: #dc2626;
              }
              .timeline {
                list-style: none;
                padding: 0;
                margin: 0;
              }
              .timeline-item {
                display: flex;
                align-items: center;
                margin-bottom: 15px;
                padding: 10px;
                background: white;
                border-radius: 6px;
                border-left: 3px solid #4A90E2;
              }
              .timeline-icon {
                width: 30px;
                height: 30px;
                background: #e0f2f1;
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                margin-right: 15px;
                font-size: 14px;
                color: #0d9488;
              }
              .timeline-content h4 {
                margin: 0 0 5px 0;
                font-size: 14px;
                font-weight: 600;
                color: #1f2937;
              }
              .timeline-content p {
                margin: 0;
                font-size: 12px;
                color: #6b7280;
              }
              .customer-info {
                background: white;
                padding: 15px;
                border-radius: 6px;
                border: 1px solid #e5e7eb;
              }
              .customer-header {
                display: flex;
                align-items: center;
                margin-bottom: 15px;
              }
              .customer-avatar {
                width: 40px;
                height: 40px;
                background: #4A90E2;
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                margin-right: 15px;
                color: white;
                font-size: 16px;
              }
              .customer-details h3 {
                margin: 0 0 5px 0;
                font-size: 16px;
                font-weight: 600;
                color: #1f2937;
              }
              .customer-details p {
                margin: 0;
                font-size: 12px;
                color: #6b7280;
              }
              .contact-list {
                list-style: none;
                padding: 0;
                margin: 0;
              }
              .contact-item {
                display: flex;
                align-items: center;
                margin-bottom: 8px;
                font-size: 13px;
                color: #1f2937;
              }
              .contact-icon {
                margin-right: 10px;
                color: #6b7280;
              }
              .notes-section {
                background: white;
                padding: 15px;
                border-radius: 6px;
                border: 1px solid #e5e7eb;
              }
              .notes-content {
                background: #f8fafc;
                padding: 15px;
                border-radius: 6px;
                border: 1px solid #e2e8f0;
                font-size: 13px;
                line-height: 1.6;
                color: #1f2937;
              }
              @media print {
                body { margin: 0; padding: 15px; }
                .section { break-inside: avoid; }
              }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>Order #${order.teckit_no}</h1>
              <p>Created on ${formatDate(order.created_at)}</p>
            </div>
            
            <div class="content">
              <div class="left-column">
                <div class="section">
                  <h2>Order Overview</h2>
                  <div class="info-grid">
                    <div class="info-item">
                      <div class="info-label">Item Name</div>
                      <div class="info-value">${order.item_name}</div>
                    </div>
                    <div class="info-item">
                      <div class="info-label">Quantity</div>
                      <div class="info-value">${order.quantity}</div>
                    </div>
                    <div class="info-item">
                      <div class="info-label">Unit Price</div>
                      <div class="info-value">AED {formatCurrency(order.unit_price || 0)}</div>
                    </div>
                    <div class="info-item">
                      <div class="info-label">Total Value</div>
                      <div class="info-value">AED {formatCurrency((order.quantity || 0) * (order.unit_price || 0))}</div>
                    </div>
                    <div class="info-item">
                      <div class="info-label">Status</div>
                      <div class="info-value">
                        <span class="status-badge status-${order.status}">${order.status.charAt(0).toUpperCase() + order.status.slice(1)}</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                ${order.campaign_name || order.voucher_name ? `
                <div class="section">
                  <h2>Campaign & Voucher Details</h2>
                  <div class="info-grid">
                    ${order.campaign_name ? `
                    <div class="info-item">
                      <div class="info-label">Campaign Name</div>
                      <div class="info-value">${order.campaign_name}</div>
                    </div>
                    ` : ''}
                    ${order.voucher_name ? `
                    <div class="info-item">
                      <div class="info-label">Voucher Name</div>
                      <div class="info-value">${order.voucher_name}</div>
                    </div>
                    ` : ''}
                    ${order.voucher_number ? `
                    <div class="info-item">
                      <div class="info-label">Voucher Number</div>
                      <div class="info-value">${order.voucher_number}</div>
                    </div>
                    ` : ''}
                    ${order.period ? `
                    <div class="info-item">
                      <div class="info-label">Period</div>
                      <div class="info-value">${order.period}</div>
                    </div>
                    ` : ''}
                  </div>
                </div>
                ` : ''}
                
                <div class="section">
                  <h2>Order Timeline</h2>
                  <ul class="timeline">
                    <li class="timeline-item">
                      <div class="timeline-icon">📅</div>
                      <div class="timeline-content">
                        <h4>Order Created</h4>
                        <p>${formatDate(order.created_at)}</p>
                      </div>
                    </li>
                    ${order.creation_date ? `
                    <li class="timeline-item">
                      <div class="timeline-icon">📋</div>
                      <div class="timeline-content">
                        <h4>Creation Date</h4>
                        <p>${formatDate(order.creation_date)}</p>
                      </div>
                    </li>
                    ` : ''}
                    ${order.coordinated_date ? `
                    <li class="timeline-item">
                      <div class="timeline-icon">👤</div>
                      <div class="timeline-content">
                        <h4>Coordinated Date</h4>
                        <p>${formatDate(order.coordinated_date)}</p>
                      </div>
                    </li>
                    ` : ''}
                    ${order.feedback_date ? `
                    <li class="timeline-item">
                      <div class="timeline-icon">✅</div>
                      <div class="timeline-content">
                        <h4>Feedback Date</h4>
                        <p>${formatDate(order.feedback_date)}</p>
                      </div>
                    </li>
                    ` : ''}
                  </ul>
                </div>
              </div>
              
              <div class="right-column">
                <div class="section">
                  <h2>Customer Information</h2>
                  ${order.customer ? `
                  <div class="customer-info">
                    <div class="customer-header">
                      <div class="customer-avatar">👤</div>
                      <div class="customer-details">
                        <h3>${order.customer.company_name || order.customer.contact_person}</h3>
                        <p>Customer ID: ${order.customer.id}</p>
                      </div>
                    </div>
                    <ul class="contact-list">
                      ${order.customer.email ? `
                      <li class="contact-item">
                        <span class="contact-icon">📧</span>
                        ${order.customer.email}
                      </li>
                      ` : ''}
                      ${order.customer.phone ? `
                      <li class="contact-item">
                        <span class="contact-icon">📞</span>
                        ${order.customer.phone}
                      </li>
                      ` : ''}
                      ${order.customer.company_name ? `
                      <li class="contact-item">
                        <span class="contact-icon">🏢</span>
                        ${order.customer.company_name}
                      </li>
                      ` : ''}
                    </ul>
                  </div>
                  ` : `
                  <p style="color: #6b7280; font-size: 14px;">No customer information available</p>
                  `}
                </div>
                
                <div class="section">
                  <h2>Order Details</h2>
                  <div class="info-item">
                    <div class="info-label">Ticket Number</div>
                    <div class="info-value">${order.teckit_no}</div>
                  </div>
                  <div class="info-item">
                    <div class="info-label">Created By</div>
                    <div class="info-value">${order.created_by || 'N/A'}</div>
                  </div>
                  <div class="info-item">
                    <div class="info-label">Coordinated By</div>
                    <div class="info-value">${order.coordinated_by || 'N/A'}</div>
                  </div>
                  <div class="info-item">
                    <div class="info-label">Feedback By</div>
                    <div class="info-value">${order.feedback_by || 'N/A'}</div>
                  </div>
                  <div class="info-item">
                    <div class="info-label">Followed By</div>
                    <div class="info-value">${order.followed_by || 'N/A'}</div>
                  </div>
                </div>
                
                ${order.notes ? `
                <div class="section">
                  <h2>Notes</h2>
                  <div class="notes-section">
                    <div class="notes-content">${order.notes}</div>
                  </div>
                </div>
                ` : ''}
              </div>
            </div>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    }
  };

  if (loading) {
    return (
      <Box sx={{ 
        backgroundColor: '#f0f9ff', 
        minHeight: '100vh', 
        p: 3, 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center' 
      }}>
        <Typography>Loading order details...</Typography>
      </Box>
    );
  }

  if (!order) {
    return (
      <Box sx={{ 
        backgroundColor: '#f0f9ff', 
        minHeight: '100vh', 
        p: 3, 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center' 
      }}>
        <Typography>Order not found</Typography>
      </Box>
    );
  }

  const statusInfo = getStatusColor(order.status);

  return (
    <Box sx={{ backgroundColor: '#f0f9ff', minHeight: '100vh', p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link 
            color="inherit" 
            href="/orders" 
            onClick={(e) => { e.preventDefault(); navigate('/orders'); }}
            sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            Orders
          </Link>
          <Typography color="text.primary">Order Details</Typography>
        </Breadcrumbs>
        
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton 
              onClick={() => navigate('/orders')}
              sx={{ 
                backgroundColor: 'white', 
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                '&:hover': { backgroundColor: '#f8fafc' }
              }}
            >
              <ArrowBack />
            </IconButton>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 600, color: '#1f2937' }}>
                Order #{order.teckit_no}
              </Typography>
              <Typography variant="body2" sx={{ color: '#6b7280' }}>
                Created on {formatDate(order.created_at)}
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<Edit />}
              onClick={() => navigate(`/orders/edit/${order.id}`)}
              sx={{
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 500,
                px: 3,
                py: 1.5,
                borderColor: '#e2e8f0',
                color: '#64748b',
                '&:hover': {
                  borderColor: '#cbd5e1',
                  backgroundColor: '#f8fafc',
                },
              }}
            >
              Edit Order
            </Button>
            <Button
              variant="contained"
              startIcon={<Print />}
              onClick={handlePrint}
              sx={{
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 500,
                px: 3,
                py: 1.5,
                backgroundColor: '#4A90E2',
                '&:hover': {
                  backgroundColor: '#3b82f6',
                },
              }}
            >
              Print
            </Button>
          </Box>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Left Column - Order Information */}
        <Grid size={{ xs: 12, md: 8 }}>
          {/* Order Overview */}
          <Card sx={{ 
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)', 
            borderRadius: '16px',
            backgroundColor: 'white',
            mb: 3
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1f2937' }}>
                  Order Overview
                </Typography>
                <Chip
                  icon={statusInfo.icon}
                  label={order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                  sx={{
                    backgroundColor: statusInfo.bg,
                    color: statusInfo.color,
                    fontWeight: 600,
                    px: 2,
                    py: 1,
                    height: 'auto',
                    '& .MuiChip-icon': {
                      color: statusInfo.color,
                    },
                  }}
                />
              </Box>

              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500, mb: 1 }}>
                      Item Name
                    </Typography>
                    <Typography variant="h6" sx={{ color: '#1f2937', fontWeight: 600 }}>
                      {order.item_name}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500, mb: 1 }}>
                      Quantity
                    </Typography>
                    <Typography variant="h6" sx={{ color: '#1f2937', fontWeight: 600 }}>
                      {order.quantity}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500, mb: 1 }}>
                      Unit Price
                    </Typography>
                    <Typography variant="h6" sx={{ color: '#1f2937', fontWeight: 600 }}>
                      {formatCurrency(order.unit_price || 0)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500, mb: 1 }}>
                      Total Value
                    </Typography>
                    <Typography variant="h6" sx={{ color: '#1f2937', fontWeight: 600 }}>
                      {formatCurrency((order.quantity || 0) * (order.unit_price || 0))}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Campaign & Voucher Details */}
          {(order.campaign_name || order.voucher_name) && (
            <Card sx={{ 
              boxShadow: '0 2px 12px rgba(0,0,0,0.08)', 
              borderRadius: '16px',
              backgroundColor: 'white',
              mb: 3
            }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1f2937', mb: 3 }}>
                  Campaign & Voucher Details
                </Typography>
                <Grid container spacing={3}>
                  {order.campaign_name && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500, mb: 1 }}>
                          Campaign Name
                        </Typography>
                        <Typography variant="body1" sx={{ color: '#1f2937' }}>
                          {order.campaign_name}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  {order.voucher_name && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500, mb: 1 }}>
                          Voucher Name
                        </Typography>
                        <Typography variant="body1" sx={{ color: '#1f2937' }}>
                          {order.voucher_name}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  {order.voucher_number && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500, mb: 1 }}>
                          Voucher Number
                        </Typography>
                        <Typography variant="body1" sx={{ color: '#1f2937' }}>
                          {order.voucher_number}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  {order.period && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500, mb: 1 }}>
                          Period
                        </Typography>
                        <Typography variant="body1" sx={{ color: '#1f2937' }}>
                          {order.period}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* Timeline */}
          <Card sx={{ 
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)', 
            borderRadius: '16px',
            backgroundColor: 'white'
          }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1f2937', mb: 3 }}>
                Order Timeline
              </Typography>
              <List sx={{ p: 0 }}>
                <ListItem sx={{ px: 0, py: 2 }}>
                  <ListItemIcon>
                    <Avatar sx={{ backgroundColor: '#e0f2f1', width: 40, height: 40 }}>
                      <CalendarToday sx={{ color: '#0d9488' }} />
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText
                    primary="Order Created"
                    secondary={formatDate(order.created_at)}
                    primaryTypographyProps={{ fontWeight: 600, color: '#1f2937' }}
                    secondaryTypographyProps={{ color: '#6b7280' }}
                  />
                </ListItem>
                {order.creation_date && (
                  <ListItem sx={{ px: 0, py: 2 }}>
                    <ListItemIcon>
                      <Avatar sx={{ backgroundColor: '#dbeafe', width: 40, height: 40 }}>
                        <Assignment sx={{ color: '#2563eb' }} />
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary="Creation Date"
                      secondary={formatDate(order.creation_date)}
                      primaryTypographyProps={{ fontWeight: 600, color: '#1f2937' }}
                      secondaryTypographyProps={{ color: '#6b7280' }}
                    />
                  </ListItem>
                )}
                {order.coordinated_date && (
                  <ListItem sx={{ px: 0, py: 2 }}>
                    <ListItemIcon>
                      <Avatar sx={{ backgroundColor: '#fef3c7', width: 40, height: 40 }}>
                        <Person sx={{ color: '#d97706' }} />
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary="Coordinated Date"
                      secondary={formatDate(order.coordinated_date)}
                      primaryTypographyProps={{ fontWeight: 600, color: '#1f2937' }}
                      secondaryTypographyProps={{ color: '#6b7280' }}
                    />
                  </ListItem>
                )}
                {order.feedback_date && (
                  <ListItem sx={{ px: 0, py: 2 }}>
                    <ListItemIcon>
                      <Avatar sx={{ backgroundColor: '#e0f2f1', width: 40, height: 40 }}>
                        <CheckCircle sx={{ color: '#0d9488' }} />
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary="Feedback Date"
                      secondary={formatDate(order.feedback_date)}
                      primaryTypographyProps={{ fontWeight: 600, color: '#1f2937' }}
                      secondaryTypographyProps={{ color: '#6b7280' }}
                    />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column - Customer & Additional Info */}
        <Grid size={{ xs: 12, md: 4 }}>
          {/* Customer Information */}
          <Card sx={{ 
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)', 
            borderRadius: '16px',
            backgroundColor: 'white',
            mb: 3
          }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1f2937', mb: 3 }}>
                Customer Information
              </Typography>
              {order.customer ? (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{ backgroundColor: '#4A90E2', mr: 2 }}>
                      <Person />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#1f2937' }}>
                        {order.customer.company_name || order.customer.contact_person}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#6b7280' }}>
                        Customer ID: {order.customer.id}
                      </Typography>
                    </Box>
                  </Box>
                  <Divider sx={{ my: 2 }} />
                  <List sx={{ p: 0 }}>
                    {order.customer.email && (
                      <ListItem sx={{ px: 0, py: 1 }}>
                        <ListItemIcon>
                          <Email sx={{ color: '#6b7280', fontSize: 20 }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={order.customer.email}
                          primaryTypographyProps={{ fontSize: '0.875rem', color: '#1f2937' }}
                        />
                      </ListItem>
                    )}
                    {order.customer.phone && (
                      <ListItem sx={{ px: 0, py: 1 }}>
                        <ListItemIcon>
                          <Phone sx={{ color: '#6b7280', fontSize: 20 }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={order.customer.phone}
                          primaryTypographyProps={{ fontSize: '0.875rem', color: '#1f2937' }}
                        />
                      </ListItem>
                    )}
                    {order.customer.company_name && (
                      <ListItem sx={{ px: 0, py: 1 }}>
                        <ListItemIcon>
                          <Business sx={{ color: '#6b7280', fontSize: 20 }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={order.customer.company_name}
                          primaryTypographyProps={{ fontSize: '0.875rem', color: '#1f2937' }}
                        />
                      </ListItem>
                    )}
                  </List>
                </Box>
              ) : (
                <Typography variant="body2" sx={{ color: '#6b7280' }}>
                  No customer information available
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Order Details */}
          <Card sx={{ 
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)', 
            borderRadius: '16px',
            backgroundColor: 'white',
            mb: 3
          }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1f2937', mb: 3 }}>
                Order Details
              </Typography>
              <List sx={{ p: 0 }}>
                <ListItem sx={{ px: 0, py: 1 }}>
                  <ListItemText
                    primary="Ticket Number"
                    secondary={order.teckit_no}
                    primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500, color: '#6b7280' }}
                    secondaryTypographyProps={{ fontSize: '0.875rem', color: '#1f2937' }}
                  />
                </ListItem>
                <ListItem sx={{ px: 0, py: 1 }}>
                  <ListItemText
                    primary="Created By"
                    secondary={order.created_by || 'N/A'}
                    primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500, color: '#6b7280' }}
                    secondaryTypographyProps={{ fontSize: '0.875rem', color: '#1f2937' }}
                  />
                </ListItem>
                <ListItem sx={{ px: 0, py: 1 }}>
                  <ListItemText
                    primary="Coordinated By"
                    secondary={order.coordinated_by || 'N/A'}
                    primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500, color: '#6b7280' }}
                    secondaryTypographyProps={{ fontSize: '0.875rem', color: '#1f2937' }}
                  />
                </ListItem>
                <ListItem sx={{ px: 0, py: 1 }}>
                  <ListItemText
                    primary="Feedback By"
                    secondary={order.feedback_by || 'N/A'}
                    primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500, color: '#6b7280' }}
                    secondaryTypographyProps={{ fontSize: '0.875rem', color: '#1f2937' }}
                  />
                </ListItem>
                <ListItem sx={{ px: 0, py: 1 }}>
                  <ListItemText
                    primary="Followed By"
                    secondary={order.followed_by || 'N/A'}
                    primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500, color: '#6b7280' }}
                    secondaryTypographyProps={{ fontSize: '0.875rem', color: '#1f2937' }}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>

          {/* Notes */}
          {order.notes && (
            <Card sx={{ 
              boxShadow: '0 2px 12px rgba(0,0,0,0.08)', 
              borderRadius: '16px',
              backgroundColor: 'white'
            }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1f2937', mb: 2 }}>
                  Notes
                </Typography>
                <Paper sx={{ 
                  p: 2, 
                  backgroundColor: '#f8fafc', 
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0'
                }}>
                  <Typography variant="body2" sx={{ color: '#1f2937', lineHeight: 1.6 }}>
                    {order.notes}
                  </Typography>
                </Paper>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default OrderDetail;
